// regelfil: src/AGENTS.md
import { useEffect, useRef, useState } from "react";
import { getFormToken, recordFormStepFn } from "@/lib/form-token.functions";

export type FormGuardScope =
  | "interest"
  | "lead"
  | "review"
  | "review-report"
  | "job-application"
  | "content-report"
  | "open-house";

export type FormGuard = {
  /** Fälten som ska skickas med i inskicket. */
  fields: () => { formToken: string; companyWebsite: string };
  honeypot: string;
  setHoneypot: (value: string) => void;
  ready: boolean;
  scope: FormGuardScope;
  token: string;
};

/**
 * Hämtar en serverutfärdad, signerad formulärbiljett när formuläret monteras.
 * Tidsspärren mäts på servern utifrån biljettens utfärdandetid.
 */
export function useFormGuard(scope: FormGuardScope, enabled = true, subject = ""): FormGuard {
  const [token, setToken] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const requested = useRef(false);

  useEffect(() => {
    // `enabled=false` gör hämtningen lat: biljetten begärs först när
    // formuläret faktiskt öppnas (annars ett serveranrop per renderat kort).
    if (!enabled || requested.current) return;
    requested.current = true;
    // Ingen avbrottsflagga i städfunktionen: i utvecklingsläget monterar React
    // (StrictMode) om varje komponent direkt, och då hann flaggan slå av det
    // enda anropet innan biljetten kom fram — formuläret blev omöjligt att
    // skicka i förhandsvisningen. Att sätta state efter en avmontering är
    // ofarligt i React 18.
    getFormToken({ data: { scope, subject } })
      .then((res) => setToken(res.token))
      .catch(() => {
        /* Utan biljett avvisas inskicket av servern med ett tydligt fel. */
      });
  }, [scope, enabled, subject]);

  return {
    fields: () => ({ formToken: token, companyWebsite: honeypot }),
    honeypot,
    setHoneypot,
    ready: token.length > 0,
    scope,
    token,
  };
}

/**
 * Mätningen i tre steg: påbörjat (första tecknet i ett fält), försök (skicka
 * tryckt) och lyckat (raden skapad — räknas av databasen). Sidladdningar
 * räknas inte: sökrobotar laddar sidor men skriver aldrig i fälten.
 * Varje steg bokförs en gång per formulärbiljett. Får aldrig störa formuläret.
 */
export function useFormSteps(guard: FormGuard, anchor: React.RefObject<HTMLElement | null>) {
  const sent = useRef<{ started: boolean; attempted: boolean }>({ started: false, attempted: false });
  const pending = useRef<Set<"started" | "attempted">>(new Set());
  const tokenRef = useRef(guard.token);
  tokenRef.current = guard.token;

  const send = (step: "started" | "attempted") => {
    if (sent.current[step]) return;
    if (!tokenRef.current) {
      pending.current.add(step);
      return;
    }
    sent.current[step] = true;
    recordFormStepFn({ data: { scope: guard.scope, token: tokenRef.current, step, school: schoolFromPath() } }).catch(
      () => undefined,
    );
  };

  useEffect(() => {
    if (!guard.token) return;
    for (const step of [...pending.current]) send(step);
    pending.current.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guard.token]);

  useEffect(() => {
    const form = anchor.current?.closest("form");
    if (!form) return;
    const onInput = (e: Event) => {
      const t = e.target as HTMLElement | null;
      // Honeypot-fältet räknas inte — det fylls bara i av bottar.
      if (t?.closest("[data-honeypot]")) return;
      send("started");
    };
    const onSubmit = () => send("attempted");
    form.addEventListener("input", onInput, true);
    form.addEventListener("submit", onSubmit, true);
    return () => {
      form.removeEventListener("input", onInput, true);
      form.removeEventListener("submit", onSubmit, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor, guard.scope]);
}

/** Skolans slug ur adressen (/skola/<slug>/…). Formulär om en skola ligger alltid där. */
export function schoolFromPath(path = typeof window === "undefined" ? "" : window.location.pathname): string | null {
  const m = /^\/skola\/([^/?#]+)/.exec(path);
  return m ? decodeURIComponent(m[1]!) : null;
}

/** Dolt honeypot-fält. Fylls bara i av bottar. Bär också formulärets stegmätning. */
export function HoneypotField({ guard, name = "company-website" }: { guard: FormGuard; name?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useFormSteps(guard, ref);
  return (
    <div ref={ref} data-honeypot aria-hidden className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
      <label htmlFor={name}>Lämna tomt</label>
      <input
        id={name}
        name={name}
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={guard.honeypot}
        onChange={(e) => guard.setHoneypot(e.target.value)}
      />
    </div>
  );
}
